/**
 * Sandboxes v2 live sandboxes: the org's claiming/ready sandboxes, filterable
 * to the viewer's own and to one application. Each row opens the detail
 * page, where the logins are.
 *
 * @module features/sandboxes/components/v2/LiveSandboxesSection
 */
import React from 'react';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PillToggle,
  Select,
  Text,
  useToast,
  type PillToggleOption,
} from '@chatsdet/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { ArrowSquareOut, DotsThree, Trash } from '@phosphor-icons/react';
import {
  useDestroySandboxV2,
  useSandboxV2Sandboxes,
  type SandboxV2Application,
  type SandboxV2ListItem,
} from '../../hooks/useSandboxesV2';
import { formatRelative } from '../overview/time-format';
import { validateSandboxesListSearch } from '../../sandboxesRouteSearch';
import { SandboxStatusChip, versionName } from './sandboxV2Display';

type Scope = 'mine' | 'all';

const SCOPE_OPTIONS: readonly PillToggleOption<Scope>[] = [
  { value: 'mine', label: 'Mine' },
  { value: 'all', label: 'All' },
];

const ALL_APPLICATIONS = 'all';

/** The http(s) ports a ready sandbox exposes, `web` first. */
function openablePorts(portUrls: Record<string, string>): Array<{ name: string; url: string }> {
  return Object.entries(portUrls)
    .filter(([, url]) => /^https?:\/\//i.test(url))
    .map(([name, url]) => ({ name, url }))
    .sort((a, b) => Number(b.name === 'web') - Number(a.name === 'web') || a.name.localeCompare(b.name));
}

export function LiveSandboxesSection({ applications }: { applications: SandboxV2Application[] }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const search = validateSandboxesListSearch(useSearch({ strict: false }));
  const scope: Scope = search.mine === false ? 'all' : 'mine';
  const applicationId = search.app && applications.some((a) => a.id === search.app) ? search.app : ALL_APPLICATIONS;
  const setFilters = (next: { scope?: Scope; applicationId?: string }) => {
    const nextScope = next.scope ?? scope;
    const nextApp = next.applicationId ?? applicationId;
    void navigate({
      to: '/sandboxes',
      search: {
        view: search.view,
        mine: nextScope === 'all' ? false : undefined,
        app: nextApp === ALL_APPLICATIONS ? undefined : nextApp,
      },
    });
  };
  const [destroyTarget, setDestroyTarget] = React.useState<SandboxV2ListItem | null>(null);

  const query = useSandboxV2Sandboxes({
    mine: scope === 'mine',
    applicationId: applicationId === ALL_APPLICATIONS ? null : applicationId,
  });
  const destroy = useDestroySandboxV2();
  const rows = React.useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const applicationName = React.useMemo(
    () => new Map(applications.map((application) => [application.id, application.name])),
    [applications]
  );

  const handleDestroy = () => {
    if (!destroyTarget) return;
    destroy.mutate(destroyTarget.id, {
      onSuccess: () => {
        toast.success('Sandbox is being destroyed');
        setDestroyTarget(null);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const columns: ColumnDef<StockFeatures, SandboxV2ListItem>[] = [
    {
      id: 'application',
      header: 'Application',
      meta: { flex: 1.2, minWidth: 140 },
      cell: ({ row }) => (
        <Link
          to="/sandboxes/v2/$sandboxId"
          params={{ sandboxId: row.original.id }}
          className="truncate text-sm font-medium text-primary hover:underline"
        >
          {applicationName.get(row.original.applicationId) ?? 'Unknown application'}
        </Link>
      ),
    },
    {
      accessorKey: 'environment',
      header: 'Environment',
      meta: { flex: 0.8, minWidth: 100 },
      cell: ({ row }) => (
        <Text size="sm" className="truncate">
          {row.original.environment}
        </Text>
      ),
    },
    {
      id: 'build',
      header: 'Build',
      meta: { flex: 1, minWidth: 110 },
      cell: ({ row }) => {
        const { version, versionTags } = row.original;
        return (
          <Text size="sm" className="truncate font-mono" title={version}>
            {versionName(versionTags, version)}
          </Text>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { flex: 0, minWidth: 110, maxWidth: 110 },
      cell: ({ row }) => <SandboxStatusChip status={row.original.status} error={row.original.error} />,
    },
    {
      id: 'owner',
      header: 'Owner',
      meta: { flex: 1.2, minWidth: 130 },
      cell: ({ row }) => {
        const { owner, status } = row.original;
        if (!owner) {
          return (
            <Text size="sm" className="text-muted-foreground">
              —
            </Text>
          );
        }
        if (owner.kind === 'user') {
          return (
            <Text size="sm" className="truncate text-muted-foreground">
              {owner.label}
            </Text>
          );
        }
        if (owner.kind === 'run') {
          const keptForDebugging = owner.status === 'failed' && status === 'ready';
          return (
            <div className="flex min-w-0 flex-col">
              <Link
                to="/flows/$workflowId"
                params={{ workflowId: owner.workflowId }}
                search={{ runId: owner.id }}
                className="truncate text-sm font-medium text-primary hover:underline"
              >
                {owner.label}
              </Link>
              {keptForDebugging && (
                <Text size="xs" className="text-muted-foreground">
                  kept for debugging
                </Text>
              )}
            </div>
          );
        }
        return (
          <Link
            to="/exploratory/$exploratoryTestId"
            params={{ exploratoryTestId: owner.id }}
            className="truncate text-sm font-medium text-primary hover:underline"
          >
            {owner.label}
          </Link>
        );
      },
    },
    {
      accessorKey: 'leaseExpiresAt',
      header: 'Expires',
      meta: { flex: 0, minWidth: 100, maxWidth: 100 },
      cell: ({ row }) => (
        <Text size="sm" className="whitespace-nowrap text-muted-foreground">
          {row.original.leaseExpiresAt ? formatRelative(row.original.leaseExpiresAt) : '—'}
        </Text>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { flex: 0, minWidth: 180, maxWidth: 180 },
      cell: ({ row }) => {
        const sandbox = row.original;
        const label = `${applicationName.get(sandbox.applicationId) ?? 'sandbox'} ${sandbox.environment}`;
        const ports = sandbox.status === 'ready' ? openablePorts(sandbox.portUrls) : [];
        const [primary, ...otherPorts] = ports;
        return (
          <Flex gap="xs" justify="end" align="center" className="w-full flex-nowrap">
            {primary && (
              <a
                href={primary.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${primary.name} for ${label} (opens in new tab)`}
                className="inline-flex min-w-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-1 text-xs text-primary hover:underline"
              >
                Open {primary.name}
                <ArrowSquareOut size={12} />
              </a>
            )}
            <MenuTrigger>
              <Button variant="ghost" size="sm" iconOnly aria-label={`Actions for ${label}`}>
                <DotsThree size={16} weight="bold" />
              </Button>
              <Menu
                onAction={(key) => {
                  if (key === 'destroy') {
                    setDestroyTarget(sandbox);
                    return;
                  }
                  const port = otherPorts.find((candidate) => candidate.name === String(key));
                  if (port) window.open(port.url, '_blank', 'noopener,noreferrer');
                }}
              >
                {otherPorts.map((port) => (
                  <MenuItem key={port.name} id={port.name}>
                    <ArrowSquareOut size={14} className="mr-2" />
                    Open {port.name}
                  </MenuItem>
                ))}
                {otherPorts.length > 0 ? <MenuSeparator /> : null}
                <MenuItem id="destroy" destructive>
                  <Trash size={14} className="mr-2" />
                  Destroy
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </Flex>
        );
      },
    },
  ];

  const applicationItems = [
    { id: ALL_APPLICATIONS, label: 'All applications' },
    ...applications.map((application) => ({ id: application.id, label: application.name })),
  ];

  return (
    <section className="flex flex-col gap-3" aria-labelledby="sandbox-v2-live">
      <Flex align="center" justify="between" wrap="wrap" gap="sm">
        <Heading level={3} id="sandbox-v2-live">
          Live sandboxes
        </Heading>
        <Flex align="center" gap="sm">
          <PillToggle aria-label="Sandbox owner" value={scope} onChange={(next) => setFilters({ scope: next })} options={SCOPE_OPTIONS} />
          <Select
            aria-label="Filter by application"
            items={applicationItems}
            selectedKey={applicationId}
            onSelectionChange={(key) => setFilters({ applicationId: String(key) })}
            className="w-48"
          />
        </Flex>
      </Flex>

      {query.isLoading ? (
        <Flex justify="center" className="py-8">
          <LoadingOrbit size="md" />
        </Flex>
      ) : query.isError ? (
        <Text color="muted">{query.error.message}</Text>
      ) : rows.length === 0 ? (
        <EmptyState
          size="sm"
          title="No live sandboxes"
          description="Claim one from an application above."
        />
      ) : (
        <>
          <DataTable columns={columns} data={rows} getRowId={(row) => row.id} pageSize={1000} />
          {query.hasNextPage && (
            <Flex justify="center">
              <Button variant="outline" size="sm" onPress={() => query.fetchNextPage()} isDisabled={query.isFetchingNextPage}>
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </Flex>
          )}
        </>
      )}

      <DialogTrigger
        isOpen={destroyTarget !== null}
        onOpenChange={(open) => !open && setDestroyTarget(null)}
        size="sm"
      >
        <span />
        <Dialog title="Destroy sandbox">
          <DialogContent>
            <Text>
              Destroy
              {destroyTarget ? ` ${applicationName.get(destroyTarget.applicationId) ?? 'unknown'} / ${destroyTarget.environment}` : ' this sandbox'}
              ? Its data is discarded permanently.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setDestroyTarget(null)} isDisabled={destroy.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onPress={handleDestroy} isDisabled={destroy.isPending}>
              {destroy.isPending ? 'Destroying…' : 'Destroy'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </section>
  );
}
